import React, { useCallback, useEffect, useState } from "react";
import { connect } from "react-redux";
import { Modal } from "antd";
import { Document as DocumentIcon } from "assets/icons";
import YmsDrawer from "components/drawer";
import RenderTable from "components/table";
import { bindActionCreators } from "redux";
import { Creators as DocumentActions } from "store/document/document-duck";
import { IEditDocument, IItems } from "store/document/document-model";
import { Creators as UserSessionActions } from "store/user-session/user-session-duck";
import useTranslation from "utils/hooks/useTranslation";
import EditForm from "../components/editForm";
import ColumnsTable from "./columnsTable";
import * as S from "./styled/index.styled";

interface IPropsScreenDocumentGrid {
  items?: Array<IItems>;
  totalItems?: number;
  orderBy?: string;
  ascending?: string;
  itemsByPage?: number;
  pageIndex?: number;
  isAdmin?: boolean;
  form?: any;
  searchFilter?: string;
  formEditModified: boolean;
  editDocument: IEditDocument;
  visibleDrawerEditDocument: boolean;
  maskAllowLetters: boolean;
  maskAllowNumbers: boolean;
  maskAllowSpecialCharacters: boolean;
  maskMinCharacters: number;
  maskMaxCharacters: number;
  isLoadingTable: boolean;
}
interface IDispatchProps {
  documentFetchAllActivesAsync: (...args) => void;
  documentDeleteByIdAsync: (...args) => void;
  documentChangeFieldValue: (...args) => void;
  documentShowDrawerEditDocument: (...args) => void;
  documentHideDrawerEditDocument: (...args) => void;
  handleOnConfirmDrawerUpdateDocument: (...args) => void;
  documentMaskAllowLetterActive: (...args) => void;
  documentMaskAllowNumberActive: (...args) => void;
  documentMaskAllowSpecialCharactersActive: (...args) => void;
  documentMaskMinCharactersActive: (...args) => void;
  documentMaskMaxCharactersActive: (...args) => void;
  documentUpdateFormModified: (...args) => void;
  documentFetchByIdAsync: (...args) => void;
  documentIdActive: (...args) => void;
}

type IProps = IPropsScreenDocumentGrid & IDispatchProps;

const DocumentGrid = ({
  form,
  items,
  isAdmin,
  totalItems,
  searchFilter,
  pageIndex,
  editDocument,
  visibleDrawerEditDocument,
  formEditModified,
  maskAllowLetters,
  maskAllowNumbers,
  maskAllowSpecialCharacters,
  maskMinCharacters,
  maskMaxCharacters,
  isLoadingTable,
  documentHideDrawerEditDocument,
  documentShowDrawerEditDocument,
  documentFetchAllActivesAsync,
  documentChangeFieldValue,
  documentDeleteByIdAsync,
  handleOnConfirmDrawerUpdateDocument,
  documentMaskAllowLetterActive,
  documentMaskAllowNumberActive,
  documentMaskAllowSpecialCharactersActive,
  documentMaskMinCharactersActive,
  documentMaskMaxCharactersActive,
  documentUpdateFormModified,
  documentFetchByIdAsync,
  documentIdActive,
}: IProps) => {
  const [visibleModalDelete, setVisibleModalDelete] = useState(false);
  const [visibleModalEditDocument, setVisibleModalEditDocument] = useState(false);
  const [selectedDocument, setSelectedDocument] = useState({ description: null, id: null });
  const DOCUMENT = useTranslation({ id: "you_are_deleting_the_document" });

  const fetchDocument = useCallback(() => documentFetchAllActivesAsync(), [documentFetchAllActivesAsync]);
  useEffect(() => {
    fetchDocument();
  }, [fetchDocument]);

  useEffect(() => {
    if (searchFilter !== "" || searchFilter === null) {
      documentChangeFieldValue("pageIndex", 1);
      fetchDocument();
    }
  }, [searchFilter]);

  const handleTableChange = (pagination, sorter) => {
    documentChangeFieldValue("orderBy", sorter.field);
    documentChangeFieldValue("ascend", sorter.order);
    documentChangeFieldValue("itemsByPage", pagination.pageSize);
    documentChangeFieldValue("pageIndex", pagination.current);
    fetchDocument();
  };

  const handleClick = ({ id, description }) => {
    setSelectedDocument({ description: description, id: id });
    setVisibleModalDelete(true);
  };

  const handleClickEdit = (data) => {
    documentFetchByIdAsync(data.id);
    documentShowDrawerEditDocument();
  };

  const handleClickConfirmDelete = () => {
    documentDeleteByIdAsync(selectedDocument);
    setVisibleModalDelete(false);
  };

  const handleClickCancelEditDocument = () => {
    if (formEditModified) {
      setVisibleModalEditDocument(true);
    } else {
      setVisibleModalEditDocument(false);
      documentHideDrawerEditDocument();
    }
  };

  const handleConfirmModalEditDocument = () => {
    setVisibleModalEditDocument(false);
    documentHideDrawerEditDocument();
  };

  return (
    <S.DocumentContainer>
      <RenderTable
        columnsTable={ColumnsTable({ handleClick, handleClickEdit, isAdmin })}
        items={items}
        totalItems={totalItems}
        handleTableChange={handleTableChange}
        pageIndex={pageIndex}
        isLoadingTable={isLoadingTable}
      />
      <Modal
        title={useTranslation({ id: "confirmation_message_delete_document" })}
        centered
        maskClosable={false}
        open={visibleModalDelete}
        onOk={() => handleClickConfirmDelete()}
        onCancel={() => setVisibleModalDelete(false)}
        okText={useTranslation({ id: "confirm" })}
        cancelText={useTranslation({ id: "cancel" })}
        destroyOnClose={true}
      >
        <p>{`${DOCUMENT}: ${selectedDocument.description}`}</p>
      </Modal>
      <YmsDrawer
        width="680"
        confirmbuttontxt={useTranslation({ id: "save" })}
        cancelbuttontxt={useTranslation({ id: "cancel" })}
        maskClosable={true}
        disabled={false}
        destroyOnClose={true}
        onConfirm={handleOnConfirmDrawerUpdateDocument}
        title={useTranslation({ id: "edit_document" })}
        placement="right"
        icon={<DocumentIcon style={S.DocumentIcon} />}
        closable={false}
        onClose={handleClickCancelEditDocument}
        open={visibleDrawerEditDocument}
      >
        <EditForm
          form={form}
          maskAllowLetters={maskAllowLetters}
          maskAllowNumbers={maskAllowNumbers}
          maskAllowSpecialCharacters={maskAllowSpecialCharacters}
          maskMinCharacters={maskMinCharacters}
          maskMaxCharacters={maskMaxCharacters}
          editDocument={editDocument}
          documentMaskAllowLetterActive={documentMaskAllowLetterActive}
          documentMaskAllowNumberActive={documentMaskAllowNumberActive}
          documentMaskAllowSpecialCharactersActive={documentMaskAllowSpecialCharactersActive}
          documentMaskMinCharactersActive={documentMaskMinCharactersActive}
          documentMaskMaxCharactersActive={documentMaskMaxCharactersActive}
          documentUpdateFormModified={documentUpdateFormModified}
          documentIdActive={documentIdActive}
        />
      </YmsDrawer>
      <Modal
        title={useTranslation({ id: "edit_document" })}
        maskClosable={false}
        centered
        open={visibleModalEditDocument}
        onCancel={() => setVisibleModalEditDocument(false)}
        onOk={() => handleConfirmModalEditDocument()}
        okText={useTranslation({ id: "yes" })}
        cancelText={useTranslation({ id: "no" })}
        destroyOnClose={true}
      >
        <p>{useTranslation({ id: "cancel_edit" })}</p>
      </Modal>
      <button data-testid="drawer-edit-document" onClick={handleClickCancelEditDocument} style={S.FakeBtnConfirm} />
    </S.DocumentContainer>
  );
};

const mapDispatchToActions = (dispatch: any) => bindActionCreators({ ...DocumentActions, ...UserSessionActions }, dispatch);

const mapStateToProps = (state) => ({
  items: state.Document.items,
  totalItems: state.Document.totalItems,
  itemsByPage: state.Document.filter.itemsByPage,
  pageIndex: state.Document.filter.pageIndex,
  orderBy: state.Document.filter.orderBy,
  ascending: state.Document.filter.ascending,
  isAdmin: state.UserSession.isAdmin,
  searchFilter: state.Document.searchFilter,
  editDocument: state.Document.editDocument,
  visibleDrawerEditDocument: state.Document.visibleDrawerEditDocument,
  formEditModified: state.Document.formEditModified,
  maskAllowLetters: state.Document.maskAllowLetters,
  maskAllowNumbers: state.Document.maskAllowNumbers,
  maskAllowSpecialCharacters: state.Document.maskAllowSpecialCharacters,
  maskMinCharacters: state.Document.maskMinCharacters,
  maskMaxCharacters: state.Document.maskMaxCharacters,
  isLoadingTable: state.Document.isLoadingTable,
});

export default connect(mapStateToProps, mapDispatchToActions)(DocumentGrid);
